// One dashboard filter per user, wherever they look at it: the server
// keeps it (the `dashboard_filter` preference, at a revision that grows
// with every write), each client shows its own copy at once and follows
// the server's.
//
// `FilterSync` is that for one page. It is given how to read and write the
// server's filter, where the copy is kept and whom to tell, so it reaches
// for no storage, URL or network itself. What a copy and a server answer
// come to is `reconcile` (logic/dashFilter.ts).
//
// - Only `set` (the user's choice) changes the filter; opening changes
//   nothing stored.
// - One write is on its way at a time. A change made meanwhile waits, and
//   of several only the latest is sent, from the revision the write
//   before it produced: two writes can never land out of order.
// - A write names the revision it was made from. When the server has moved
//   past it (another device wrote), the server's filter is read and the
//   user's change is made again on top of it (`rebase`): what the other
//   device chose stays, and the user's latest word stands where both
//   touched a value. Nothing is ever written over a revision unread.
// - A revision older than the one held is never shown, and nothing is read
//   over a change not yet sent.
import { rebase, reconcile, sameFilter, NO_FILTER, type DashFilter, type ServerFilter } from '../logic/dashFilter';
import type { CachedFilter } from '../logic/dashFilter';

/** Whether the filter shown is the server's (`saved`), on its way there, or could not be sent (`failed`: `retry` sends it). */
export type SaveStatus = 'saved' | 'saving' | 'failed';

export interface FilterSyncDeps {
  /** The server's filter and its revision. */
  read(): Promise<ServerFilter>;
  /** Make `filter` the server's, from revision `from`: the new revision, or `conflict` when the server is at another. */
  write(filter: DashFilter, from: number): Promise<{ rev: number } | 'conflict'>;
  /** This client's copy. */
  cache: { load(): CachedFilter; save(copy: CachedFilter): void };
  /** The filter or its save status changed. */
  onchange(state: { current: DashFilter; status: SaveStatus }): void;
  now(): number;
}

/** Writes refused for the revision before a change is given up as failed (each is retried on the revision just read). */
const CONFLICTS_MAX = 3;

export class FilterSync {
  /** The user's filter, as this page knows it. */
  current: DashFilter = NO_FILTER;
  status: SaveStatus = 'saved';
  /** The server's revision `current` is of, or was changed from. */
  private rev = 0;
  /** `current` holds a change the server has not confirmed, made at `at`. */
  private unsent = false;
  private at = 0;
  /** The server's filter at `rev`, which an unsent change was made from; null when not known. */
  private base: DashFilter | null = null;
  /** A write is on its way (or the read a refused one asks for). */
  private writing = false;
  private closed = false;
  /** Reads and writes under way, with what their answers lead to (see `idle`); and who waits for none. */
  private busy = 0;
  private idlers: (() => void)[] = [];

  constructor(private deps: FilterSyncDeps) {}

  /**
   * Start from this client's copy, and return the filter to show now. The
   * server is then asked: a later revision replaces the copy (`onchange`),
   * and a change the copy holds unsent is sent. Nothing is written
   * otherwise.
   */
  open(): DashFilter {
    const copy = this.deps.cache.load();
    this.current = copy.filter;
    this.rev = copy.rev;
    this.unsent = copy.unsent;
    this.at = copy.at;
    this.base = copy.unsent ? copy.base : copy.filter;
    void this.track(this.follow());
    return this.current;
  }

  /** The user chose `filter`: it is the filter now, remembered and sent. Choosing the one held does nothing. */
  set(filter: DashFilter) {
    if (sameFilter(filter, this.current)) return;
    // The first of a run of changes is made from the server's filter; the rest from the same.
    if (!this.unsent) this.base = this.current;
    this.current = filter;
    this.unsent = true;
    this.at = this.deps.now();
    this.keep();
    // While a write is on its way this one waits for it; either way the page hears once.
    if (this.writing) this.tell();
    else void this.track(this.send());
  }

  /** Send again a change that could not be sent. */
  retry() {
    if (this.unsent) void this.track(this.send());
  }

  /** The server's filter may have changed (another page of the user's said so): read it, and take a later revision. */
  refresh(): Promise<void> {
    return this.track(this.follow());
  }

  /**
   * Resolves once nothing this page started is under way: no read or write
   * awaits its answer, nor does what an answer led to (a write after a
   * read, a read after a refused write). At once when nothing is.
   */
  idle(): Promise<void> {
    return this.busy ? new Promise((r) => this.idlers.push(r)) : Promise.resolve();
  }

  private track(work: Promise<void>): Promise<void> {
    this.busy++;
    return work.finally(() => {
      if (--this.busy === 0) for (const r of this.idlers.splice(0)) r();
    });
  }

  /** Stop: answers still on their way are dropped. */
  close() {
    this.closed = true;
  }

  private keep() {
    this.deps.cache.save({ filter: this.current, rev: this.rev, unsent: this.unsent, at: this.unsent ? this.at : 0, base: this.unsent ? this.base : null });
  }

  private tell(status: SaveStatus = this.status) {
    this.status = status;
    if (!this.closed) this.deps.onchange({ current: this.current, status });
  }

  private async send(): Promise<void> {
    if (this.writing || this.closed) return;
    this.writing = true;
    this.tell('saving');
    let status: SaveStatus = 'saved';
    try {
      for (let refused = 0; this.unsent && !this.closed; ) {
        const filter = this.current;
        const answer = await this.deps.write(filter, this.rev);
        if (answer === 'conflict') {
          if (++refused > CONFLICTS_MAX) throw new Error('the filter kept changing elsewhere');
          // Another device wrote: its filter is read, and the user's change made again on it.
          const theirs = await this.deps.read();
          const on = theirs.filter ?? NO_FILTER;
          const next = rebase(this.base ?? on, this.current, on);
          const changed = !sameFilter(next, this.current);
          this.current = next;
          this.base = on;
          this.rev = theirs.rev;
          this.keep();
          if (changed) this.tell();
          continue;
        }
        this.rev = answer.rev;
        // The server holds `filter` now: a change made meanwhile was made from it, and is the next write.
        this.base = filter;
        if (sameFilter(this.current, filter)) this.unsent = false;
        this.keep();
      }
    } catch {
      status = 'failed';
    } finally {
      this.writing = false;
    }
    this.tell(status);
  }

  private async follow(): Promise<void> {
    if (this.writing || this.closed || this.status === 'failed') return;
    let server: ServerFilter;
    try {
      server = await this.deps.read();
    } catch {
      // Unreachable: what is shown stays, and so does the mark on an unsent copy.
      return;
    }
    // A change made while it was read is on its way, from a revision at least this one.
    if (this.closed || this.writing) return;
    const held: CachedFilter = { filter: this.current, rev: this.rev, unsent: this.unsent, at: this.at, base: this.base };
    const next = reconcile(held, server, this.deps.now());
    if (next.send) {
      if (!this.unsent) this.at = this.deps.now();
      // Sent onto the revision just read: `reconcile` made the change again on its filter where that differs.
      const changed = !sameFilter(next.filter, this.current);
      this.current = next.filter;
      this.base = server.filter ?? NO_FILTER;
      this.unsent = true;
      this.rev = server.rev;
      this.keep();
      if (changed) this.tell();
      return this.send();
    }
    if (!this.unsent && server.rev <= this.rev) return;
    const changed = !sameFilter(next.filter, this.current);
    this.current = next.filter;
    this.rev = Math.max(server.rev, this.rev);
    this.base = next.filter;
    this.unsent = false;
    this.keep();
    if (changed) this.tell();
  }
}
